import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Tooltip,
  useTheme,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

interface StatsCardProps {
  title: string;
  label: string;
  value: number | string;
  currency?: '€' | '$';
  tooltip?: string;
  trend?: number;
  icon?: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'info';

  secondaryLabel?: string;
  secondaryValue?: number | string;
  secondaryCurrency?: '€' | '$';
}

const variantColors = {
  default: (theme: any) => ({
    border: theme.palette.divider,
    accent: theme.palette.text.secondary,
    bgGradient:
      theme.palette.mode === 'dark'
        ? 'linear-gradient(to bottom right, #1e293b, #0f172a)'
        : 'linear-gradient(to bottom right, #f1f5f9, #ffffff)',
  }),
  success: (theme: any) => ({
    border: theme.palette.success.light,
    accent: theme.palette.success.dark,
    bgGradient:
      theme.palette.mode === 'dark'
        ? 'linear-gradient(to bottom right, #0d3f36, #112c26)'
        : 'linear-gradient(to bottom right, #e6f4f1, #ffffff)',
  }),
  warning: (theme: any) => ({
    border: theme.palette.warning.light,
    accent: theme.palette.warning.dark,
    bgGradient:
      theme.palette.mode === 'dark'
        ? 'linear-gradient(to bottom right, #4b2f05, #3a2200)'
        : 'linear-gradient(to bottom right, #fff9e6, #ffffff)',
  }),
  info: (theme: any) => ({
    border: theme.palette.info.light,
    accent: theme.palette.info.dark,
    bgGradient:
      theme.palette.mode === 'dark'
        ? 'linear-gradient(to bottom right, #12344d, #1e293b)'
        : 'linear-gradient(to bottom right, #e7f3fd, #ffffff)',
  }),
};

export function StatsCard({
  title,
  label,
  value,
  currency,
  tooltip,
  trend,
  icon,
  variant = 'default',
  secondaryLabel,
  secondaryValue,
  secondaryCurrency,
}: StatsCardProps) {
  const theme = useTheme();
  const colors = variantColors[variant](theme);

  return (
<Card
  elevation={0}
  sx={{
    height: '100%', // 👉 remplit entièrement la hauteur de la cellule Grid
    borderRadius: 4,
    border: `1px solid ${colors.border}`,
    background: colors.bgGradient,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[4],
      transform: 'translateY(-3px) scale(1.01)',
    },
  }}
>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(circle at top right, rgba(255,255,255,0.1), transparent)`,
          zIndex: 1,
        }}
      />
      <CardContent sx={{ position: 'relative', zIndex: 2, p: 3 }}>
        {/* Title */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
        {icon && (
            <Box
            sx={{
                p: 1,
                borderRadius: 2,
                bgcolor:
                theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: colors.accent,
            }}
            >
            {icon}
            </Box>
        )}
        <Typography variant="subtitle1" fontWeight={600} color="text.primary">
            {title}
        </Typography>
        </Box>
          {tooltip && (
            <Tooltip title={tooltip} arrow>
              <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          )}
        </Box>

        {/* Main Stat: Label + Value */}
        <Box display="flex" alignItems="baseline" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            {typeof value === 'number' ? value.toLocaleString() : value} {currency ?? ''}
          </Typography>
        </Box>

        {/* Secondary Stat: Value + Label */}
        {secondaryLabel && secondaryValue !== undefined && (
          <Box display="flex" alignItems="baseline" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {secondaryLabel}
            </Typography>
            <Typography variant="body1" fontWeight={600} color="text.primary">
              {typeof secondaryValue === 'number' ? secondaryValue.toLocaleString() : secondaryValue} {secondaryCurrency ?? ''}
            </Typography>
          </Box>
        )}

        {/* Trend */}
        {trend !== undefined && (
          <Box
            mt={2}
            display="flex"
            alignItems="center"
            gap={0.5}
            color={trend >= 0 ? 'success.main' : 'error.main'}
            fontSize={14}
            fontWeight={500}
          >
            <ArrowUpwardIcon
              sx={{
                fontSize: 18,
                transform: trend < 0 ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            />
            <span>{trend >= 0 ? '+' : ''}{trend}%</span>
            <Typography variant="caption" color="text.secondary">
              vs période précédente
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
